// Remove this file after the Account_Provisioning_History release.
import {createSlice} from '@reduxjs/toolkit';
import {
    getAccountActiveUserHistoryEventsByDateFailure,
    getAccountActiveUserHistoryEventsByDateRequest,
    getAccountActiveUserHistoryEventsByDateSuccess,
} from 'src/openapi/admin/apis/GetAccountActiveUserHistoryEventsApi';

import type {DailyUserHistoryEvent} from 'src/openapi/admin/models/DailyUserHistoryEvent';
import type {Error} from 'src/openapi/admin/models/Error';
import type {AppState} from 'src/store/typedef';

export type AccountActiveUserHistoryEventsCacheKey = `${number}|${string}`;

export type CacheEntry = {
    key: AccountActiveUserHistoryEventsCacheKey,
    accountId: number,
    historyDate: string,
    data: Array<DailyUserHistoryEvent> | null,
    isLoading: boolean,
    error: Error | null,
    /** Epoch millis when data was last successfully loaded */
    updatedAtMs: number | null,
};

type State = {
    cache: Record<AccountActiveUserHistoryEventsCacheKey, CacheEntry>,
};

const initialState: State = {
    cache: {},
};

const buildKey = (accountId: number, historyDate: string): AccountActiveUserHistoryEventsCacheKey => {
    return `${accountId}|${historyDate}`;
};

export const ACCOUNT_ACTIVE_USER_HISTORY_EVENTS_TTL_MS = 5 * 60 * 1000;

export const accountActiveUserHistoryEventsSlice = createSlice({
    name: 'accountActiveUserHistoryEvents',
    initialState,
    reducers: {
        clearAll: state => {
            state.cache = {};
        },
        clearByKey: (state, action: {payload: {accountId: number, historyDate: string}}) => {
            const {accountId, historyDate} = action.payload;
            const key = buildKey(accountId, historyDate);

            delete state.cache[key];
        },
    },
    extraReducers: builder => {
        builder
            .addCase(getAccountActiveUserHistoryEventsByDateRequest, (state, action) => {
                const {accountId, historyDate} = action.payload.meta;
                const key = buildKey(accountId, historyDate);

                const prev = state.cache[key];

                state.cache[key] = {
                    key,
                    accountId,
                    historyDate,
                    data: prev?.data ?? null,
                    isLoading: true,
                    error: null,
                    updatedAtMs: prev?.updatedAtMs ?? null,
                };
            })
            .addCase(getAccountActiveUserHistoryEventsByDateSuccess, (state, action) => {
                const {accountId, historyDate} = action.payload.meta;
                const key = buildKey(accountId, historyDate);

                state.cache[key] = {
                    key,
                    accountId,
                    historyDate,
                    data: action.payload.data,
                    isLoading: false,
                    error: null,
                    updatedAtMs: Date.now(),
                };
            })
            .addCase(getAccountActiveUserHistoryEventsByDateFailure, (state, action) => {
                const {accountId, historyDate} = action.payload.meta;
                const key = buildKey(accountId, historyDate);

                const prev = state.cache[key];

                state.cache[key] = {
                    key,
                    accountId,
                    historyDate,
                    data: prev?.data ?? null,
                    isLoading: false,
                    error: action.payload.data,
                    updatedAtMs: prev?.updatedAtMs ?? null,
                };
            });
    },
});

export const accountActiveUserHistoryEventsActions = accountActiveUserHistoryEventsSlice.actions;

export const selectAccountActiveUserHistoryEventsEntry = (
    state: AppState,
    accountId: number,
    historyDate: string,
) => {
    return state.accountActiveUserHistoryEvents.cache[buildKey(accountId, historyDate)];
};

export const selectAccountActiveUserHistoryEventsIsFresh = (
    state: AppState,
    accountId: number,
    historyDate: string,
    nowMs: number = Date.now(),
) => {
    const entry = selectAccountActiveUserHistoryEventsEntry(state, accountId, historyDate);
    if (!entry?.data || !entry.updatedAtMs) {
        return false;
    }

    return nowMs - entry.updatedAtMs < ACCOUNT_ACTIVE_USER_HISTORY_EVENTS_TTL_MS;
};
